import { createGalaxyInputSchema, type GalaxyKind, type GalaxyRole } from "@memory-universe/domain";
import type { Session, User } from "@supabase/supabase-js";
import { create } from "zustand";
import { isSupabaseConfigured, supabase } from "../../../shared/supabase/client";
import type { PhotoPalette } from "../../../shared/media/photoPalette";

const ACTIVE_GALAXY_STORAGE_PREFIX = "memory-universe:active-galaxy";
const LOCAL_GALAXIES_STORAGE_KEY = "memory-universe:local-galaxies:v1";
const LOCAL_ACTIVE_GALAXY_STORAGE_KEY = "memory-universe:local-active-galaxy";
const GALAXY_PALETTE_STORAGE_PREFIX = "memory-universe:galaxy-palette";
export const LOCAL_GALAXY_ID = "local-space";

export interface ActiveGalaxy {
  id: string;
  name: string;
  description: string;
  kind: GalaxyKind;
  role: GalaxyRole;
  memberCount: number;
  memoryCount: number;
  startedAt: string | null;
  latestAt: string | null;
  visualPalette: PhotoPalette | null;
}

/** @deprecated Use ActiveGalaxy. Kept while repositories migrate from space_id to galaxy_id. */
export type ActiveSpace = ActiveGalaxy;

export const localDemoGalaxy: ActiveGalaxy = {
  id: LOCAL_GALAXY_ID,
  name: "本地记忆",
  description: "只保存在这台设备上的演示星河",
  kind: "personal",
  role: "owner",
  memberCount: 1,
  memoryCount: 0,
  startedAt: null,
  latestAt: null,
  visualPalette: null
};

interface SpaceState {
  initialized: boolean;
  loading: boolean;
  session: Session | null;
  user: User | null;
  passwordRecovery: boolean;
  galaxies: ActiveGalaxy[];
  /** Current galaxy compatibility alias used by the existing timeline and media repositories. */
  space: ActiveGalaxy | null;
  error: string | null;
  notice: string | null;
  initialize: () => Promise<() => void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
  createGalaxy: (name: string, kind: GalaxyKind, description?: string) => Promise<string | null>;
  createSpace: (name: string) => Promise<string | null>;
  selectGalaxy: (galaxyId: string) => void;
  acceptInvite: (token: string) => Promise<string | null>;
  createInvite: (role?: Exclude<GalaxyRole, "owner">) => Promise<string>;
  refreshGalaxies: () => Promise<void>;
  refreshSpace: () => Promise<void>;
  updateLocalGalaxyStats: (galaxyId: string, memories: Array<{ occurredAt: string }>) => void;
  updateGalaxyVisualPalette: (galaxyId: string, palette: PhotoPalette) => void;
}

interface MembershipRow {
  space_id: string;
  role: "owner" | "editor" | "viewer" | "member";
  memory_spaces: {
    name: string;
    kind?: GalaxyKind;
    description?: string;
    archived_at?: string | null;
  };
}

function message(error: unknown) {
  return error instanceof Error ? error.message : "记忆星河暂时无法连接";
}

function activeGalaxyStorageKey(userId: string) {
  return `${ACTIVE_GALAXY_STORAGE_PREFIX}:${userId}`;
}

function getRememberedGalaxyId(userId: string) {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(activeGalaxyStorageKey(userId));
}

function rememberGalaxyId(userId: string, galaxyId: string | null) {
  if (typeof window === "undefined") return;
  const key = activeGalaxyStorageKey(userId);
  if (galaxyId) window.localStorage.setItem(key, galaxyId);
  else window.localStorage.removeItem(key);
}

function isPhotoPalette(value: unknown): value is PhotoPalette {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every((color) => typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color))
  );
}

function galaxyPaletteStorageKey(galaxyId: string) {
  return `${GALAXY_PALETTE_STORAGE_PREFIX}:${galaxyId}`;
}

function readGalaxyPalette(galaxyId: string): PhotoPalette | null {
  if (typeof window === "undefined") return null;
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(galaxyPaletteStorageKey(galaxyId)) ?? "null");
    return isPhotoPalette(value) ? value : null;
  } catch {
    return null;
  }
}

function persistGalaxyPalette(galaxyId: string, palette: PhotoPalette) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(galaxyPaletteStorageKey(galaxyId), JSON.stringify(palette));
}

function isStoredLocalGalaxy(value: unknown): value is ActiveGalaxy {
  if (!value || typeof value !== "object") return false;
  const galaxy = value as Partial<ActiveGalaxy>;
  return (
    typeof galaxy.id === "string" &&
    typeof galaxy.name === "string" &&
    typeof galaxy.description === "string" &&
    (galaxy.kind === "personal" || galaxy.kind === "shared") &&
    galaxy.role === "owner" &&
    typeof galaxy.memberCount === "number" &&
    typeof galaxy.memoryCount === "number" &&
    (galaxy.startedAt === null || typeof galaxy.startedAt === "string") &&
    (galaxy.latestAt === null || typeof galaxy.latestAt === "string") &&
    (galaxy.visualPalette === undefined || galaxy.visualPalette === null || isPhotoPalette(galaxy.visualPalette))
  );
}

function readLocalGalaxies() {
  if (typeof window === "undefined") return [localDemoGalaxy];
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(LOCAL_GALAXIES_STORAGE_KEY) ?? "null");
    if (!Array.isArray(value)) return [localDemoGalaxy];
    const galaxies = value.filter(isStoredLocalGalaxy).map((galaxy) => ({
      ...galaxy,
      visualPalette: galaxy.visualPalette ?? readGalaxyPalette(galaxy.id)
    }));
    return galaxies.length > 0 ? galaxies : [localDemoGalaxy];
  } catch {
    return [localDemoGalaxy];
  }
}

function persistLocalGalaxies(galaxies: ActiveGalaxy[], activeGalaxyId: string | null) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_GALAXIES_STORAGE_KEY, JSON.stringify(galaxies));
  if (activeGalaxyId) window.localStorage.setItem(LOCAL_ACTIVE_GALAXY_STORAGE_KEY, activeGalaxyId);
  else window.localStorage.removeItem(LOCAL_ACTIVE_GALAXY_STORAGE_KEY);
}

function initialLocalState() {
  const galaxies = readLocalGalaxies();
  const rememberedId = typeof window === "undefined" ? null : window.localStorage.getItem(LOCAL_ACTIVE_GALAXY_STORAGE_KEY);
  const space = galaxies.find((galaxy) => galaxy.id === rememberedId) ?? galaxies[0] ?? localDemoGalaxy;
  return { galaxies, space };
}

function isMissingGalaxyColumns(error: { code?: string; message?: string } | null) {
  return Boolean(
    error &&
      (error.code === "PGRST204" ||
        error.code === "42703" ||
        /kind|description|archived_at/i.test(error.message ?? ""))
  );
}

function isMissingCreateGalaxyFunction(error: { code?: string; message?: string } | null) {
  return Boolean(
    error &&
      (error.code === "PGRST202" ||
        /could not find the function public\.create_galaxy|create_galaxy.*schema cache/i.test(error.message ?? ""))
  );
}

function mapRole(role: MembershipRow["role"]): GalaxyRole {
  if (role === "owner" || role === "viewer") return role;
  return "editor";
}

async function fetchGalaxies(userId: string): Promise<ActiveGalaxy[]> {
  if (!supabase) return [];
  const client = supabase;

  const currentSchemaResult = await client
    .from("memberships")
    .select("space_id, role, memory_spaces!inner(name, kind, description, archived_at)")
    .eq("user_id", userId)
    .is("left_at", null);

  let rows: MembershipRow[];
  if (currentSchemaResult.error && isMissingGalaxyColumns(currentSchemaResult.error)) {
    const legacySchemaResult = await client
      .from("memberships")
      .select("space_id, role, memory_spaces!inner(name)")
      .eq("user_id", userId)
      .is("left_at", null);
    if (legacySchemaResult.error) throw legacySchemaResult.error;
    rows = (legacySchemaResult.data ?? []) as unknown as MembershipRow[];
  } else {
    if (currentSchemaResult.error) throw currentSchemaResult.error;
    rows = (currentSchemaResult.data ?? []) as unknown as MembershipRow[];
  }

  const activeRows = rows.filter((row) => !row.memory_spaces.archived_at);

  return Promise.all(
    activeRows.map(async (row) => {
      const [membersResult, firstMemoryResult, latestMemoryResult] = await Promise.all([
        client
          .from("memberships")
          .select("id", { count: "exact", head: true })
          .eq("space_id", row.space_id)
          .is("left_at", null),
        client
          .from("memories")
          .select("occurred_at", { count: "exact" })
          .eq("space_id", row.space_id)
          .is("deleted_at", null)
          .order("occurred_at", { ascending: true })
          .limit(1),
        client
          .from("memories")
          .select("occurred_at")
          .eq("space_id", row.space_id)
          .is("deleted_at", null)
          .order("occurred_at", { ascending: false })
          .limit(1)
      ]);
      if (membersResult.error) throw membersResult.error;
      if (firstMemoryResult.error) throw firstMemoryResult.error;
      if (latestMemoryResult.error) throw latestMemoryResult.error;
      return {
        id: row.space_id,
        name: row.memory_spaces.name,
        description: row.memory_spaces.description ?? "",
        kind: row.memory_spaces.kind ?? "shared",
        role: mapRole(row.role),
        memberCount: membersResult.count ?? 1,
        memoryCount: firstMemoryResult.count ?? 0,
        startedAt: firstMemoryResult.data?.[0]?.occurred_at ?? null,
        latestAt: latestMemoryResult.data?.[0]?.occurred_at ?? null,
        visualPalette: readGalaxyPalette(row.space_id)
      };
    })
  );
}

function chooseActiveGalaxy(userId: string, galaxies: ActiveGalaxy[]) {
  const rememberedId = getRememberedGalaxyId(userId);
  const active = galaxies.find((galaxy) => galaxy.id === rememberedId) ?? galaxies[0] ?? null;
  rememberGalaxyId(userId, active?.id ?? null);
  return active;
}

export const useSpaceStore = create<SpaceState>((set, get) => ({
  initialized: !isSupabaseConfigured,
  loading: false,
  session: null,
  user: null,
  passwordRecovery: false,
  galaxies: isSupabaseConfigured ? [] : initialLocalState().galaxies,
  space: isSupabaseConfigured ? null : initialLocalState().space,
  error: null,
  notice: null,

  initialize: async () => {
    if (!supabase) return () => undefined;
    set({ loading: true });
    const { data, error } = await supabase.auth.getSession();
    if (error) set({ error: error.message, loading: false, initialized: true });
    else {
      const user = data.session?.user ?? null;
      try {
        const galaxies = user ? await fetchGalaxies(user.id) : [];
        set({
          session: data.session,
          user,
          galaxies,
          space: user ? chooseActiveGalaxy(user.id, galaxies) : null,
          loading: false,
          initialized: true,
          error: null
        });
      } catch (nextError) {
        set({ session: data.session, user, galaxies: [], space: null, loading: false, initialized: true, error: message(nextError) });
      }
    }
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      const user = session?.user ?? null;
      const previousUserId = get().user?.id ?? null;
      if (!user) {
        set({ session: null, user: null, passwordRecovery: false, galaxies: [], space: null });
        return;
      }
      const passwordRecovery = event === "PASSWORD_RECOVERY" ? true : get().passwordRecovery;
      if (previousUserId && previousUserId !== user.id) {
        set({ session, user, passwordRecovery, galaxies: [], space: null });
      } else {
        // Token refreshes are not a loss of galaxy access. Keeping the active
        // galaxy prevents route guards from redirecting an in-progress flow.
        set({ session, user, passwordRecovery });
      }
      void get().refreshGalaxies();
    });
    return () => listener.subscription.unsubscribe();
  },

  signIn: async (email, password) => {
    if (!supabase) return;
    set({ loading: true, error: null, notice: null });
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) set({ loading: false, error: error.message });
    else set({ loading: false });
  },

  signUp: async (email, password) => {
    if (!supabase) return;
    set({ loading: true, error: null, notice: null });
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) set({ loading: false, error: error.message });
    else set({ loading: false });
  },

  requestPasswordReset: async (email) => {
    if (!supabase) {
      set({ error: "本地演示模式无法发送密码重置邮件。", notice: null });
      return;
    }
    set({ loading: true, error: null, notice: null });
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      // The configured Site URL is the recovery destination. Keeping this
      // exact avoids widening the redirect allow-list for password recovery.
      redirectTo: window.location.origin
    });
    if (error) set({ loading: false, error: error.message });
    else set({ loading: false, notice: "重置邮件已发送，请打开邮箱中的链接设置新密码。" });
  },

  updatePassword: async (password) => {
    if (!supabase) return;
    set({ loading: true, error: null, notice: null });
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    set({ loading: false, passwordRecovery: false, notice: "密码已更新，正在进入记忆宇宙。" });
  },

  signOut: async () => {
    if (!supabase) return;
    const userId = get().user?.id;
    await supabase.auth.signOut();
    if (userId) rememberGalaxyId(userId, null);
    set({ session: null, user: null, passwordRecovery: false, galaxies: [], space: null, error: null, notice: null });
  },

  createGalaxy: async (name, kind, description = "") => {
    const normalizedName = name.trim();
    const normalizedDescription = description.trim();
    const parsedInput = createGalaxyInputSchema.safeParse({ name: normalizedName, kind, description: normalizedDescription });
    if (!parsedInput.success) {
      set({ loading: false, error: "请填写 1—48 个字的星河名称，简介不能超过 280 个字。" });
      return null;
    }

    if (!supabase) {
      const galaxy: ActiveGalaxy = {
        id: crypto.randomUUID(),
        name: parsedInput.data.name,
        description: parsedInput.data.description,
        kind: parsedInput.data.kind,
        role: "owner",
        memberCount: 1,
        memoryCount: 0,
        startedAt: null,
        latestAt: null,
        visualPalette: null
      };
      const galaxies = [...get().galaxies, galaxy];
      persistLocalGalaxies(galaxies, galaxy.id);
      set({ galaxies, space: galaxy, loading: false, error: null });
      return galaxy.id;
    }

    set({ loading: true, error: null });

    let result = await supabase.rpc("create_galaxy", {
      galaxy_name: normalizedName,
      galaxy_kind: kind,
      galaxy_description: normalizedDescription
    });

    if (result.error && isMissingCreateGalaxyFunction(result.error)) {
      if (kind === "personal") {
        set({ loading: false, error: "个人星河需要先在 Supabase 应用多星河迁移（004）。" });
        return null;
      }
      result = await supabase.rpc("create_memory_space", { space_name: normalizedName });
    }

    if (result.error) {
      set({ loading: false, error: result.error.message });
      return null;
    }

    const user = get().user;
    if (user && typeof result.data === "string") rememberGalaxyId(user.id, result.data);
    await get().refreshGalaxies();
    set({ loading: false });
    return typeof result.data === "string" ? result.data : null;
  },

  createSpace: async (name) => get().createGalaxy(name, "shared"),

  selectGalaxy: (galaxyId) => {
    const { galaxies, user } = get();
    const nextGalaxy = galaxies.find((galaxy) => galaxy.id === galaxyId);
    if (!nextGalaxy) return;
    if (user) rememberGalaxyId(user.id, nextGalaxy.id);
    else persistLocalGalaxies(galaxies, nextGalaxy.id);
    set({ space: nextGalaxy, error: null });
  },

  acceptInvite: async (token) => {
    if (!supabase) return null;
    set({ loading: true, error: null });
    const { data, error } = await supabase.rpc("accept_space_invite", { raw_token: token.trim() });
    if (error) {
      set({ loading: false, error: error.message });
      return null;
    }
    else {
      const user = get().user;
      if (user && typeof data === "string") rememberGalaxyId(user.id, data);
      await get().refreshGalaxies();
      set({ loading: false });
      return typeof data === "string" ? data : null;
    }
  },

  createInvite: async (role = "editor") => {
    const space = get().space;
    if (!supabase || !space) throw new Error("请先创建共同星河");
    if (space.kind !== "shared") throw new Error("个人星河不能生成成员邀请");
    const { data, error } = await supabase.rpc("create_space_invite", { target_space_id: space.id, target_role: role });
    if (error) throw new Error(error.message);
    return `${window.location.origin}/join?token=${encodeURIComponent(data as string)}`;
  },

  refreshGalaxies: async () => {
    const user = get().user;
    if (!user) return;
    try {
      const galaxies = await fetchGalaxies(user.id);
      set({ galaxies, space: chooseActiveGalaxy(user.id, galaxies), error: null });
    } catch (error) {
      set({ error: message(error) });
    }
  },

  refreshSpace: async () => get().refreshGalaxies(),

  updateLocalGalaxyStats: (galaxyId, memories) => {
    if (isSupabaseConfigured) return;
    const dates = memories.map((memory) => memory.occurredAt).sort();
    const memoryCount = memories.length;
    const startedAt = dates[0] ?? null;
    const latestAt = dates.at(-1) ?? null;
    const currentGalaxy = get().galaxies.find((galaxy) => galaxy.id === galaxyId);
    if (
      !currentGalaxy ||
      (currentGalaxy.memoryCount === memoryCount &&
        currentGalaxy.startedAt === startedAt &&
        currentGalaxy.latestAt === latestAt)
    ) return;
    const galaxies = get().galaxies.map((galaxy) => galaxy.id === galaxyId ? {
      ...galaxy,
      memoryCount,
      startedAt,
      latestAt
    } : galaxy);
    const space = galaxies.find((galaxy) => galaxy.id === get().space?.id) ?? null;
    persistLocalGalaxies(galaxies, space?.id ?? null);
    set({ galaxies, space });
  },

  updateGalaxyVisualPalette: (galaxyId, palette) => {
    persistGalaxyPalette(galaxyId, palette);
    const galaxies = get().galaxies.map((galaxy) => galaxy.id === galaxyId ? {
      ...galaxy,
      visualPalette: palette
    } : galaxy);
    const space = galaxies.find((galaxy) => galaxy.id === get().space?.id) ?? get().space;
    if (!isSupabaseConfigured) persistLocalGalaxies(galaxies, space?.id ?? null);
    set({ galaxies, space });
  }
}));
